import React, { useEffect, useState, useCallback} from 'react';
import { useAuth } from "../../context/AuthContext";
import ProductosList from './ProductosList';
import ImageUploader from './ImageUploader';
import ImageGallery from './ImageGallery';
import SearchFilters from './SearchFilters';
import { getProductImages, deleteProductImage, uploadProductImage, updateImageUsage } from '../../services/Mercadeo/imageService';
import { Product } from '../../types/product';
import { Image, updateUsoImagen, UsoImagenType, getUsoImagen } from '../../types/image';
import { ProductFilters } from '../../types/productFilters';
import { fetchProductsAll, fetchProductsPaginated, deleteImageProduct, postImageProduct } from '../../services/Mercadeo/productoService';
import Swal from 'sweetalert2';
import { mapOracleRowToProducto } from "../../utils/mapper";

import * as Sentry from '@sentry/react';

import * as XLSX from 'xlsx';
const saveAs = require("file-saver").saveAs;



interface FileWithUsage {
  file: File;
  usoImagen: UsoImagenType;
}

const ImageManager: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productImages, setProductImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [filters, setFilters] = useState<ProductFilters>({ searchTerm: '', page: 1, pageSize: 15, hasImages: undefined });
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  
  const { user } = useAuth();
  
  const currentPage = filters.page;

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setProducts([]);
    const start = Date.now();

    try {


      Sentry.addBreadcrumb({
        category: 'fetch productos',
        message: `Cargando productos con filtros: ${JSON.stringify(filters)}`,
        level: 'info',
        data: {
          page: filters.page,
          pageSize: filters.pageSize,
          searchTerm: filters.searchTerm,
          hasImages: filters.hasImages
        }
      });
      console.log('Filtros aplicados: ', filters);
      const productsData: any = await fetchProductsPaginated(filters);
      
        
      console.log('Productos cargados: ', productsData.data);
      const products: Product[] = (productsData.data || []).map(mapOracleRowToProducto);

      setProducts(products);
      setTotalPages(productsData.pagination.totalPages)
      setTotalItems(productsData.pagination.totalItems)
      setSelectedProduct(null)
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error loading products:', error);
    } finally {
      const elapsed = Date.now() - start;
      Sentry.captureMessage(`Productos cargados en ${elapsed} ms`, {
        level: 'info'      
      });
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadProducts(); }, [loadProducts]);


  const loadProductImages = useCallback(async (sku: string) => {
    try {
      setLoadingImages(true);
      console.log('Producto Seleccionado: ', sku);

      const images = await getProductImages(sku);

      console.log('Imágenes obtenidas de getProductImages: ', images);

      const imageWithUso = images.map(img => ({
        ...img,
        //usoImagen: getUsoImagen(img),
        Metadata: { ...img.Metadata, usoimagen: getUsoImagen(img) }
      }));

      setProductImages(imageWithUso);

      console.log('Imagenes cargadas en setProductImages: ', imageWithUso);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error loading images:', error);
      setProductImages([]);
    } finally {
      setLoadingImages(false);
    }
  }, []);


  useEffect(() => {
    if (selectedProduct) loadProductImages(selectedProduct.productCode);
  }, [selectedProduct, loadProductImages]);




  const handleUpload = async (files: FileWithUsage[]) => {
    console.log('llegue a handleUpload en ImageManage.ts');

    if (!selectedProduct) return;
    setUploading(true);

    try {

      // Carga los archivos a S3
      await uploadProductImage(selectedProduct.productCode, files, user?.User.email || 'admin');

      // Luego registra las imágenes en Oracle
      const uploadPromises = files.map(async (fileWithUsage) => {
        const result = await postImageProduct({
          sku: selectedProduct.productCode,
          imageName: fileWithUsage.file.name,
          usoImagen: fileWithUsage.usoImagen,
          usuario: user?.User.email || 'admin'
        });
        console.info('Resultado de postImageProduct', result);
      });

      //Carga los URLs a Oracle
      await Promise.all(uploadPromises);



      await loadProductImages(selectedProduct.productCode);

      setProducts(prev => prev.map(p =>
        p.productCode === selectedProduct.productCode ? { ...p, hasImages: true } : p
      ));

    } catch (error:any) {
      console.error('Error uploading images:', error.response.data);
      throw error.response.data;
    } finally {
      setUploading(false);
    }
  };

  const handleOnImageUsoChange = async (imageName: string, usoImagen: UsoImagenType) => {
    console.log('Cambio de uso de imagen solicitado: ', imageName, usoImagen);

    if (!selectedProduct) return;

    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: `Se cambiará el uso de la imagen ${imageName} a ${usoImagen}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      Swal.fire({
        icon: 'info',
        title: 'Acción cancelada',
        toast: true,
        position: 'bottom',
        showConfirmButton: false,

        timer: 2000,
        timerProgressBar: true
      });
      return;
    }

    try {
      setLoadingImages(true);
      // Mostrar loading
      Swal.fire({
        title: 'Actualizando...',
        text: 'Actualizando tipo de imagen',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      //LLama al Service updateImageUsage donde se actualiza en S3 y Oracle
      const result = await updateImageUsage(selectedProduct!.productCode, imageName, usoImagen, user?.User.email || 'admin');


      console.log('Resultado de updateImageUsage: ', result);
      console.info('Imagenes del producto:', productImages);

      // Actualizar el estado local
      setProductImages(prev => prev.map(img =>
        img.name === imageName
          ? updateUsoImagen(img, usoImagen)
          : img
      ));

      // Cerrar loading y mostrar éxito
      Swal.fire({
        icon: 'success',
        title: '¡Actualizado!',
        text: `Tipo de imagen cambiado a "${usoImagen}"`,
        toast: true,
        position: 'top-end',
        timer: 3000
      });

      setProducts(prev => prev.map(p =>
        p.productCode === selectedProduct.productCode ? { ...p, hasImages: true } : p
      ));

    } catch (error) {
      console.error('Error updating image usage:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el tipo de imagen',
        toast: true,
        position: 'top-end',
        timer: 3000
      });
    } finally {
      setLoadingImages(false);
    }
  };



  const handleDelete = async (imageName: string) => {
    if (!selectedProduct) return;
    try {

      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: `Se eliminará la imagen seleccionada del producto ${selectedProduct?.productCode}. Esta acción no se puede deshacer.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });

      if (!result.isConfirmed) {
        await Swal.fire({
          icon: 'info',
          title: 'Acción cancelada',
          toast: true,
          position: 'bottom',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true
        });
        return;
      }

      //Borrar Imagen en S3 y Oracle
      const imagenBorrada = await deleteProductImage(imageName);
      console.log('Resultado de borrar: ', imagenBorrada);

      //Borrar Imagen en Oracle
      const imagenBorradaOracle = await deleteImageProduct(selectedProduct.productCode, imageName);

      console.log('Resultado de borrar en Oracle: ', imagenBorradaOracle);

      if (imagenBorrada.data.deletedImage === imageName) {
        // 🔄 recargar las imágenes del producto
        if (selectedProduct && selectedProduct.productCode) {
          await loadProductImages(selectedProduct.productCode);
        }
        await Swal.fire({
          icon: 'info',
          title: `Imagen elminada ${imageName}`,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
        return;
      } else {
        await Swal.fire({
          icon: 'error',
          title: `Imagen ${imageName} NO se eleminó`,
          toast: true,
          position: 'top-end',
          showConfirmButton: true,
          timer: 3000,
          timerProgressBar: true
        });
      }

    } catch (error) {
      console.log('Error borrando imagen');
      Swal.fire('Error !!!', `NO se borró la imagen ${imageName}`, 'error');
    }

    return;
  }

  const handleNextPage = async (nextPage: number) => {
    //setCurrentPage(nextPage);
    setFilters({
      ...filters,
      page: nextPage
    })
  }


  const handleExportExcel = async () => {
    setExporting(true);
    try {


      let filterMessage = '';

      if (filters.hasImages === true) {
        filterMessage = 'Solo se generará la lista con productos con imágenes';
      } else if (filters.hasImages === false) {
        filterMessage = 'Solo se generará la lista con productos SIN imágenes';
      } else {
        filterMessage = 'Se generará la lista con TODOS los productos';
      }

      // Mostrar confirmación al usuario
      const resultConfimacion = await Swal.fire({
        title: 'Generar Reporte Excel',
        html: `
            <div>
              <p>${filterMessage}</p>
              <p>¿Desea continuar con la exportación?</p>
            </div>
          `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, Generar Excel',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });

      if (resultConfimacion.isDismissed || !resultConfimacion.isConfirmed) {
        return;
      }

      const result: any = await fetchProductsAll();
      const allProducts: any = result.data;

      console.log('Todos los productos obtenidos para exportar: ', allProducts);

      if (!allProducts || !Array.isArray(allProducts)) {
        Swal.fire({
          title: 'Error',
          text: 'No se pudieron obtener los datos de productos',
          icon: 'error'
        });
        return;
      }

      let filteredProducts = [];

      if (filters.hasImages === true) {
        filteredProducts = allProducts.filter((prod: any) => prod.TIENE_IMAGENES === 'S');
      } else if (filters.hasImages === false) {
        filteredProducts = allProducts.filter((prod: any) => prod.TIENE_IMAGENES === 'N');
      } else {
        filteredProducts = allProducts; // Sin filtro
      }
      console.log('Productos para exportar: ', filteredProducts);
      console.log('Filtros aplicados en exportar: ', filters);



      const exportData = filteredProducts.map((prod: any) => ({
        'Código de Producto': prod.NO_ARTI,
        'Nombre': prod.NOMBRE,
        'Descripcion de Producto': prod.DESCRIPCION,
        'Marca': prod.MARCA,
        'Medida': prod.MEDIDA,
        'Tiene Imágenes': prod.TIENE_IMAGENES === 'S' ? 'Sí' : 'No',
        'Url Imagen': prod.URLIMAGEN

      }));



      //Crea el worksheet a partir de los datos
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      //Convertir la columna Imagenes en hyperlinks
      //Encontramos el rango
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');

      // La columna "Url Imagen" es la 7ta columna (columna G, índice 6)
      const urlImageColumnIndex = 6;

      // Recorremos todas las filas (empezando desde la fila 2 para saltar los encabezados)
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: urlImageColumnIndex });
        const cell = worksheet[cellAddress];

        if (cell && cell.v) {
          // Creamos el hyperlink
          worksheet[cellAddress].l = { Target: cell.v };
          // Opcional: Cambiar el texto que se muestra
          //worksheet[cellAddress].v = 'Ver Imagen';
        }
      }

      //Ajustar el ancho de las columnas para mejor visualización
      worksheet['!cols'] = [
        { wch: 15 }, // Código de Producto
        { wch: 40 }, // Nombre
        { wch: 40 }, // Descripción de Producto
        { wch: 30 }, // Marca
        { wch: 12 }, // Medida
        { wch: 12 }, // Tiene Imágenes
        { wch: 100 }  // Url Imagen
      ];

      //Crear wl workbook y agregar el worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Lista de Productos');

      //Convertir a buffer
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });

      saveAs(dataBlob, 'Lista_Productos.xlsx');

    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      setExporting(false);
    }
  }

  

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <h1 className="h2 mb-3">
            Administrador de Imágenes de Productos
          </h1>
          <SearchFilters filters={filters} onFilterChange={setFilters} loading={loading} />

        </div>
        <div className='col-12 col-md-2 mb-2 d-flex align-items-end'>
          <button
            className='btn btn-success w-100'
            onClick={handleExportExcel}
            disabled={exporting}
          >
            Exportar a excel
          </button>
        </div>
      </div>

      <div className="row position-relative g-0">
        <div className='col-md-5'>
          <div className="card me-2">
            <div className="card-header bg-light">
              <h4 className="h5 mb-0">
                Productos ({products.length} )
                {filters.hasImages !== undefined && (
                  <span className="badge bg-primary ms-2">
                    {filters.hasImages ? 'Con imágenes' : 'Sin imágenes'}
                  </span>
                )}
              </h4>
            </div>
            <div className="card-body p-0">
              {loadingImages && (
                <div className="position-absolute top-0 start-0 w-100 h-100 bg-light bg-opacity-50 d-flex align-items-center justify-content-center" style={{ zIndex: 10 }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando imágenes...</span>
                  </div>
                </div>
              )}
              <div style={{ opacity: loadingImages ? '0.6' : '', pointerEvents: loadingImages ? 'none' : 'auto' }} >
                <ProductosList
                  products={products}
                  selectedProduct={selectedProduct}
                  onSelectProduct={(p) => {
                    //evita que se haga click cuando esta en proceso de traer las imagenes
                    //if (loadingImages) return;
                    setSelectedProduct(p)
                  }}
                  loading={loading}
                />
              </div>

            </div>
            {totalItems > products.length && (
              <div className="card-footer">
                <nav aria-label="Page navigation">
                  <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handleNextPage(currentPage - 1)}>
                        Anterior
                      </button>
                    </li>
                    <li className="page-item">
                      <span className="page-link">
                        Página {currentPage} de {totalPages}
                      </span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => handleNextPage(currentPage + 1)}>
                        Siguiente
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-7">
          {selectedProduct && (
            <div className="card">
              <div className="card-header bg-light">
                <h2 className="h5 mb-0">
                  Imágenes para: {selectedProduct.productName} ({selectedProduct.productCode})
                </h2>
              </div>
              <div className="card-body">
                <ImageUploader product={selectedProduct} onUpload={handleUpload} uploading={uploading} existingImages={productImages} />
                <ImageGallery images={productImages} product={selectedProduct} loading={loadingImages} onImageDelete={handleDelete} onImageUsoChange={handleOnImageUsoChange} />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Footer fijo en la parte baja */}
      <footer className="mt-auto py-3 bg-light border-top">
        <div className="container-fluid">
          <p className="text-muted small mb-0 text-center">ImageManager.tsx</p>
        </div>
      </footer>
    </div>
  );
};

export default ImageManager;